import { useEffect, useState, useCallback, useRef, Component } from 'react';
import type { ReactNode, ErrorInfo } from 'react';
import {
    creditService,
    type CreditSummary,
    type CreditLedgerItem,
    type PricingFeature,
    type PricingPlan,
} from '../../services/creditService';
import { stripeService } from '../../services/stripeService';
import GlassCard from '../../components/ui/v2/GlassCard';
import GlassButton from '../../components/ui/v2/GlassButton';
import {
    RiCoinLine, RiArrowUpLine, RiArrowDownLine,
    RiGiftLine, RiUserAddLine, RiFileTextLine,
    RiMessage2Line, RiVideoLine, RiRobot2Line,
    RiBarChartLine, RiRefundLine, RiTimeLine,
    RiMailLine, RiRefreshLine, RiCheckLine,
    RiStarLine, RiExternalLinkLine, RiSettings4Line, RiCloseLine,
} from 'react-icons/ri';

const POLL_INTERVAL = 60_000;

// ── Helpers ────────────────────────────────────────────────────────────────

function minorToDisplay(minor: number): string {
    if (minor === 0) return '$0.00';
    if (minor < 100) return `$0.${minor.toString().padStart(2, '0')}`;
    return `$${(minor / 100).toFixed(2)}`;
}

function getPricingIcon(icon: string) {
    const map: Record<string, React.ReactNode> = {
        video:       <RiVideoLine size={18} />,
        robot:       <RiRobot2Line size={18} />,
        'file-text': <RiFileTextLine size={18} />,
        mail:        <RiMailLine size={18} />,
        message:     <RiMessage2Line size={18} />,
    };
    return map[icon] ?? <RiCoinLine size={18} />;
}

function getTxIcon(type: string) {
    const map: Record<string, React.ReactNode> = {
        resume_gen:            <RiFileTextLine size={16} />,
        cover_letter_gen:      <RiFileTextLine size={16} />,
        message_gen:           <RiMessage2Line size={16} />,
        ai_interview:          <RiRobot2Line size={16} />,
        video_interview:       <RiVideoLine size={16} />,
        video_interview_start: <RiVideoLine size={16} />,
        simulation_start:      <RiRobot2Line size={16} />,
        analytics:             <RiBarChartLine size={16} />,
        purchase:              <RiCoinLine size={16} />,
        refund:                <RiRefundLine size={16} />,
        expiry:                <RiTimeLine size={16} />,
        onboarding_bonus:      <RiGiftLine size={16} />,
        referral_bonus:        <RiUserAddLine size={16} />,
        student_allocation:    <RiUserAddLine size={16} />,
    };
    return map[type] ?? <RiCoinLine size={16} />;
}

const POSITIVE_TYPES = ['purchase', 'onboarding_bonus', 'referral_bonus', 'refund', 'student_allocation'];

function txColor(type: string) {
    return POSITIVE_TYPES.includes(type) ? 'text-emerald-400' : 'text-red-400';
}
function txIconBg(type: string) {
    return POSITIVE_TYPES.includes(type)
        ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
        : 'bg-red-500/10 border-red-500/20 text-red-400';
}
function formatDate(iso: string) {
    return new Date(iso).toLocaleDateString('en-GB', {
        day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
}

// ── Plan Card ──────────────────────────────────────────────────────────────

function PlanCard({ plan, currentPlan, onManage }: {
    plan: PricingPlan;
    currentPlan: string | null;
    onManage: () => void;
}) {
    const [upgrading, setUpgrading] = useState(false);
    const [err, setErr]             = useState<string | null>(null);

    const priceDisplay = plan.price_minor === 0
        ? 'Free'
        : `${minorToDisplay(plan.price_minor)}/mo`;

    const isCurrent = currentPlan === plan.key;

    const handleUpgrade = async () => {
        if (plan.key === 'free') return;
        setUpgrading(true);
        setErr(null);
        try {
            const url = await stripeService.createCheckout(plan.key as 'standard' | 'premium');
            window.location.href = url;
        } catch (e) {
            setErr(e instanceof Error ? e.message : 'Checkout failed');
            setUpgrading(false);
        }
    };

    return (
        <div className={`relative flex flex-col gap-4 p-5 rounded-2xl border transition-all
            ${plan.highlight
                ? 'bg-white/[0.07] border-white/[0.2] shadow-[0_0_24px_rgba(255,255,255,0.06)]'
                : 'bg-white/[0.03] border-white/[0.07]'
            }`}
        >
            {plan.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-white text-black text-[10px] font-bold uppercase tracking-widest flex items-center gap-1">
                    <RiStarLine size={10} /> Recommended
                </div>
            )}

            <div>
                <p className="text-white font-bold text-base">{plan.label}</p>
                <p className="text-zinc-500 text-xs mt-0.5">{plan.description}</p>
            </div>

            <div>
                <span className="text-3xl font-black text-white">{priceDisplay}</span>
                {plan.price_minor > 0 && (
                    <span className="text-zinc-500 text-xs ml-2">
                        {plan.credits.toLocaleString()} credits/{plan.period}
                    </span>
                )}
                {plan.price_minor === 0 && (
                    <span className="text-zinc-500 text-xs ml-2">
                        {plan.credits} credits on sign-up
                    </span>
                )}
            </div>

            {plan.price_minor > 0 && (
                <p className="text-zinc-600 text-xs -mt-2">
                    = ${(plan.price_minor / plan.credits / 100).toFixed(4)} per credit
                </p>
            )}

            <ul className="space-y-2 flex-1">
                {plan.features.map(f => (
                    <li key={f} className="flex items-start gap-2 text-xs text-zinc-400">
                        <RiCheckLine className="text-emerald-400 flex-shrink-0 mt-0.5" size={13} />
                        {f}
                    </li>
                ))}
            </ul>

            {err && <p className="text-red-400 text-xs">{err}</p>}

            {plan.key !== 'free' && (
                isCurrent ? (
                    <button
                        onClick={onManage}
                        className="w-full py-2 rounded-xl bg-white/[0.06] border border-white/[0.1] text-white/60 text-xs font-medium flex items-center justify-center gap-1.5 hover:bg-white/[0.1] transition-colors"
                    >
                        <RiSettings4Line size={13} /> Manage
                    </button>
                ) : (
                    <button
                        onClick={handleUpgrade}
                        disabled={upgrading}
                        className={`w-full py-2 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-1.5 disabled:opacity-50
                            ${plan.highlight
                                ? 'bg-white text-black hover:bg-white/90'
                                : 'bg-white/[0.07] text-white border border-white/[0.1] hover:bg-white/[0.12]'
                            }`}
                    >
                        {upgrading
                            ? <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                            : <><RiExternalLinkLine size={13} /> Upgrade</>
                        }
                    </button>
                )
            )}
        </div>
    );
}

// ── Feature pricing row ────────────────────────────────────────────────────

function FeatureRow({ feature }: { feature: PricingFeature }) {
    return (
        <div className="flex items-center gap-4 px-4 py-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
            <div className="w-9 h-9 rounded-lg bg-white/[0.05] border border-white/[0.08] flex items-center justify-center text-zinc-300 flex-shrink-0">
                {getPricingIcon(feature.icon)}
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-white/80 text-sm font-medium">{feature.label}</p>
                <p className="text-white/30 text-xs truncate">{feature.description}</p>
            </div>
            {/* Credits */}
            <div className="text-right flex-shrink-0 hidden sm:block">
                <p className="text-zinc-400 text-[10px] uppercase tracking-wider mb-0.5">Credits</p>
                <p className="text-white font-bold text-sm">{feature.cost}</p>
            </div>
            {/* Standard cost */}
            <div className="text-right flex-shrink-0">
                <p className="text-zinc-500 text-[10px] uppercase tracking-wider mb-0.5">Standard</p>
                <p className="text-white/70 text-sm font-semibold">{minorToDisplay(feature.cost_standard_minor)}</p>
            </div>
            {/* Premium cost */}
            <div className="text-right flex-shrink-0">
                <p className="text-zinc-500 text-[10px] uppercase tracking-wider mb-0.5">Premium</p>
                <p className="text-emerald-400 text-sm font-semibold">{minorToDisplay(feature.cost_premium_minor)}</p>
            </div>
        </div>
    );
}

// ── Error Boundary ─────────────────────────────────────────────────────────

class CreditsErrorBoundary extends Component<
    { children: ReactNode },
    { error: Error | null }
> {
    state = { error: null };
    static getDerivedStateFromError(error: Error) { return { error }; }
    componentDidCatch(error: Error, info: ErrorInfo) {
        console.error('[Credits] render error:', error, info);
    }
    render() {
        if (this.state.error) {
            return (
                <div className="space-y-4 max-w-3xl">
                    <h2 className="text-2xl font-bold text-white">Credits</h2>
                    <div className="px-4 py-5 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-400">
                        <p className="font-semibold mb-1">Something went wrong loading Credits</p>
                        <p className="text-xs opacity-70">{(this.state.error as Error).message}</p>
                        <button
                            onClick={() => this.setState({ error: null })}
                            className="mt-3 text-xs underline"
                        >Try again</button>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}

// ── Main ───────────────────────────────────────────────────────────────────

function CreditsInner() {
    const [summary, setSummary]         = useState<CreditSummary | null>(null);
    const [features, setFeatures]       = useState<PricingFeature[]>([]);
    const [plans, setPlans]             = useState<PricingPlan[]>([]);
    const [grants, setGrants]           = useState<{ onboarding: number; referral: number } | null>(null);
    const [items, setItems]             = useState<CreditLedgerItem[]>([]);
    const [page, setPage]               = useState(1);
    const [hasNext, setHasNext]         = useState(false);
    const [total, setTotal]             = useState(0);
    const [loading, setLoading]         = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [syncing, setSyncing]         = useState(false);
    const [lastSynced, setLastSynced]   = useState<Date | null>(null);
    const [error, setError]             = useState<string | null>(null);
    const [currentPlan, setCurrentPlan] = useState<string | null>(null);
    const [paymentNotice, setPaymentNotice] = useState<'success' | 'cancelled' | null>(null);
    const [managingBilling, setManagingBilling] = useState(false);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const loadLedger = useCallback(async (p: number, append = false) => {
        const data = await creditService.getLedger(p, 20);
        setItems(prev => append ? [...prev, ...data.items] : data.items);
        setHasNext(data.has_next);
        setTotal(data.total);
        setPage(p);
    }, []);

    const refreshSummary = useCallback(async (force = false) => {
        try {
            setSyncing(true);
            const s = await creditService.getSummary(force);
            setSummary(s);
            setLastSynced(new Date());
        } catch { /* silent */ } finally {
            setSyncing(false);
        }
    }, []);

    useEffect(() => {
        // Check for redirect from Stripe checkout
        const params = new URLSearchParams(window.location.search);
        const payment = params.get('payment');
        if (payment === 'success' || payment === 'cancelled') {
            setPaymentNotice(payment as 'success' | 'cancelled');
            // Clean URL without reloading
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, []);

    useEffect(() => {
        (async () => {
            try {
                setError(null);
                const [sum, pricing, sub] = await Promise.all([
                    creditService.getSummary(),
                    creditService.getPricing(),
                    stripeService.getSubscription().catch(() => null),
                ]);
                await loadLedger(1);
                setSummary(sum);
                setFeatures(pricing.features);
                setPlans(pricing.plans);
                setGrants(pricing.grants);
                setCurrentPlan(sub?.plan ?? 'free');
                setLastSynced(new Date());
            } catch (e: unknown) {
                const msg = e instanceof Error ? e.message : 'Failed to load credits data';
                setError(msg);
            } finally {
                setLoading(false);
            }
        })();
    }, [loadLedger]);

    const handleManageBilling = async () => {
        setManagingBilling(true);
        try {
            const url = await stripeService.createPortal();
            window.location.href = url;
        } catch {
            setManagingBilling(false);
        }
    };

    // Periodic balance sync
    useEffect(() => {
        intervalRef.current = setInterval(() => {
            creditService.getSummary(true)
                .then(s => { setSummary(s); setLastSynced(new Date()); })
                .catch(() => {});
        }, POLL_INTERVAL);
        return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
    }, []);

    const handleLoadMore = async () => {
        setLoadingMore(true);
        await loadLedger(page + 1, true);
        setLoadingMore(false);
    };

    return (
        <div className="space-y-8 max-w-3xl">

            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-white">Credits</h2>
                    <p className="text-white/40 text-sm mt-1">Balance, plans, and transaction history</p>
                </div>
                <button
                    onClick={() => refreshSummary(true)}
                    disabled={syncing}
                    className="flex items-center gap-2 text-xs text-zinc-500 hover:text-zinc-300 transition-colors disabled:opacity-40"
                >
                    <RiRefreshLine className={`text-base ${syncing ? 'animate-spin' : ''}`} />
                    {lastSynced
                        ? `Synced ${lastSynced.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                        : 'Sync'}
                </button>
            </div>

            {/* Payment notice */}
            {paymentNotice === 'success' && (
                <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm">
                    <RiCheckLine size={16} />
                    <span>Payment successful! Your credits have been added.</span>
                    <button onClick={() => setPaymentNotice(null)} className="ml-auto"><RiCloseLine size={16} /></button>
                </div>
            )}
            {paymentNotice === 'cancelled' && (
                <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.08] text-white/50 text-sm">
                    <span>Checkout cancelled — no charge was made.</span>
                    <button onClick={() => setPaymentNotice(null)} className="ml-auto"><RiCloseLine size={16} /></button>
                </div>
            )}

            {/* Error state */}
            {error && !loading && (
                <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                    <i className="ri-error-warning-line text-base flex-shrink-0"></i>
                    <span>{error}. Make sure the backend is running.</span>
                    <button
                        onClick={() => { setError(null); setLoading(true); }}
                        className="ml-auto underline underline-offset-2 hover:no-underline"
                    >Retry</button>
                </div>
            )}

            {/* Balance summary */}
            {loading ? (
                <div className="grid grid-cols-3 gap-4">
                    {[0,1,2].map(i => <div key={i} className="h-24 rounded-2xl bg-white/[0.08] animate-pulse" />)}
                </div>
            ) : summary ? (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <GlassCard className="flex flex-col gap-1">
                        <p className="text-white/40 text-xs font-medium">Available</p>
                        <p className={`text-3xl font-bold ${summary.available_credits > 0 ? 'text-blue-400' : 'text-white/50'}`}>
                            {summary.available_credits.toLocaleString()}
                        </p>
                        <p className="text-white/30 text-xs">credits to spend</p>
                    </GlassCard>
                    <GlassCard className="flex flex-col gap-1">
                        <p className="text-white/40 text-xs font-medium">Total Earned</p>
                        <p className="text-3xl font-bold text-emerald-400">+{summary.total_earned.toLocaleString()}</p>
                        <p className="text-white/30 text-xs">all time</p>
                    </GlassCard>
                    <GlassCard className="flex flex-col gap-1">
                        <p className="text-white/40 text-xs font-medium">Total Spent</p>
                        <p className="text-3xl font-bold text-red-400">-{summary.total_spent.toLocaleString()}</p>
                        <p className="text-white/30 text-xs">all time</p>
                    </GlassCard>
                </div>
            ) : null}

            {/* Plans */}
            {loading && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {[0,1,2].map(i => <div key={i} className="h-48 rounded-2xl bg-white/[0.08] animate-pulse" />)}
                </div>
            )}
            {!loading && plans.length > 0 && (
                <div>
                    <div className="flex items-center justify-between mb-4">
                        <p className="text-white/60 text-sm font-medium">Plans</p>
                        {currentPlan && currentPlan !== 'free' && (
                            <button
                                onClick={handleManageBilling}
                                disabled={managingBilling}
                                className="flex items-center gap-1.5 text-xs text-white/40 hover:text-white/70 transition-colors"
                            >
                                <RiSettings4Line size={13} />
                                {managingBilling ? 'Opening…' : 'Manage billing'}
                            </button>
                        )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {plans.map(p => (
                            <PlanCard
                                key={p.key}
                                plan={p}
                                currentPlan={currentPlan}
                                onManage={handleManageBilling}
                            />
                        ))}
                    </div>
                    {grants && (
                        <div className="mt-3 grid grid-cols-2 gap-3">
                            <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-emerald-500/[0.05] border border-emerald-500/[0.12]">
                                <RiGiftLine className="text-emerald-400 flex-shrink-0" size={16} />
                                <div className="min-w-0">
                                    <p className="text-white/70 text-xs font-medium">Welcome bonus</p>
                                    <p className="text-emerald-400 text-sm font-bold">{grants.onboarding} credits free on sign-up</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-emerald-500/[0.05] border border-emerald-500/[0.12]">
                                <RiUserAddLine className="text-emerald-400 flex-shrink-0" size={16} />
                                <div className="min-w-0">
                                    <p className="text-white/70 text-xs font-medium">Referral bonus</p>
                                    <p className="text-emerald-400 text-sm font-bold">+{grants.referral} credits per referral</p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Feature cost table */}
            {loading && (
                <div className="space-y-2">
                    {[0,1,2,3,4].map(i => <div key={i} className="h-14 rounded-xl bg-white/[0.08] animate-pulse" />)}
                </div>
            )}
            {!loading && features.length > 0 && (
                <div>
                    <div className="flex items-end justify-between mb-3">
                        <p className="text-white/60 text-sm font-medium">What each feature costs</p>
                        <p className="text-white/25 text-xs">Standard = $9.99/mo · Premium = $24.99/mo</p>
                    </div>
                    <div className="space-y-2">
                        {features.map(f => <FeatureRow key={f.key} feature={f} />)}
                    </div>
                    <p className="text-white/20 text-xs mt-3">
                        Premium saves ~17% per credit vs Standard. Prices update automatically from the server.
                    </p>
                </div>
            )}

            {/* Transaction history */}
            <div>
                <div className="flex items-center justify-between mb-3">
                    <p className="text-white/60 text-sm font-medium">
                        Transaction History
                        {total > 0 && <span className="text-white/30 ml-2">({total})</span>}
                    </p>
                </div>

                {loading ? (
                    <div className="space-y-2">
                        {[0,1,2,3,4].map(i => (
                            <div key={i} className="h-14 rounded-xl bg-white/[0.08] animate-pulse" />
                        ))}
                    </div>
                ) : items.length === 0 ? (
                    <GlassCard className="text-center py-10">
                        <RiCoinLine className="mx-auto text-white/20 mb-3" size={32} />
                        <p className="text-white/40 text-sm">No transactions yet</p>
                        <p className="text-white/25 text-xs mt-1">Credits will appear here as you use features</p>
                    </GlassCard>
                ) : (
                    <div className="space-y-1.5">
                        {items.map(item => (
                            <div
                                key={item.id}
                                className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.05] transition-colors"
                            >
                                <div className={`flex-shrink-0 w-8 h-8 rounded-lg border flex items-center justify-center ${txIconBg(item.type)}`}>
                                    {getTxIcon(item.type)}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-white/80 text-sm font-medium truncate">{item.reason}</p>
                                    <p className="text-white/30 text-xs">{formatDate(item.created_at)}</p>
                                </div>
                                <div className={`flex items-center gap-1 font-bold text-sm flex-shrink-0 ${txColor(item.type)}`}>
                                    {item.amount > 0 ? <RiArrowUpLine size={14} /> : <RiArrowDownLine size={14} />}
                                    {Math.abs(item.amount).toLocaleString()}
                                </div>
                            </div>
                        ))}

                        {hasNext && (
                            <div className="pt-2">
                                <GlassButton variant="secondary" className="w-full" onClick={handleLoadMore} loading={loadingMore}>
                                    Load more
                                </GlassButton>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default function Credits() {
    return (
        <CreditsErrorBoundary>
            <CreditsInner />
        </CreditsErrorBoundary>
    );
}
